import { useCallback, useState } from "react";

import { RiskControlsButton } from "~/v3-poc/features/risk-controls";
import { AtdSettings, useAtdPreferencesContext } from "../user-preferences";
import { ReactGridLayoutTab } from "./grid/ReactGridLayoutTab.connector";
import { Overview } from "./overview/Overview.connector";
import { GroupRegistryProvider } from "./shared/groups/GroupRegistry.context";
import { SnapshotStreamProvider } from "./shared/snapshotStream/SnapshotStream.context";
import { WorkspaceRepositoryProvider } from "./shared/workspace/WorkspaceRepository.context";
import { WorkspaceSelectionProvider } from "./shared/workspace/workspaceSelection.context";
import { useWorkspaceController } from "./Workspace.controller";
import { WorkspacePresentation } from "./Workspace.pres";
import { useWorkspaceNavController } from "./WorkspaceNav.controller";
import { OVERVIEW_NAV_VALUE } from "./WorkspaceNav.pres";
import { useWorkspaceShellController } from "./WorkspaceShell.controller";

const WorkspaceConnected = () => {
  const {
    activeView,
    selectOverview,
    selectWorkspace,
    tabs,
    canCloseTab,
    createWorkspace,
    duplicateWorkspace,
    closeWorkspace,
    renameWorkspace,
    openWorkspace,
    reorderWorkspace,
    resetToDefaults,
    resetToBlank,
  } = useWorkspaceController();

  const { navPosition, navCollapsed, setNavCollapsed } =
    useAtdPreferencesContext();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const shell = useWorkspaceShellController({
    activeView,
    tabs,
    createWorkspace,
    duplicateWorkspace,
    closeWorkspace,
    renameWorkspace,
    reorderWorkspace,
    resetToDefaults,
    resetToBlank,
  });

  const nav = useWorkspaceNavController({
    tabs,
    onReorderTab: shell.onReorderTab,
    activeTabId:
      activeView.type === "overview" ? OVERVIEW_NAV_VALUE : activeView.id,
  });

  const onToggleNavCollapsed = useCallback(() => {
    setNavCollapsed(!navCollapsed);
  }, [navCollapsed, setNavCollapsed]);

  const navMode = navPosition === "left" ? "left" : "top";
  // Only the left rail collapses, so the stored flag alone does not mean the
  // account controls are in a narrow row.
  const accountActionsCollapsed = navMode === "left" && navCollapsed;

  const activePanel =
    activeView.type === "overview" ? (
      <Overview />
    ) : (
      <ReactGridLayoutTab key={activeView.id} workspaceId={activeView.id} />
    );

  return (
    <SnapshotStreamProvider>
      <WorkspacePresentation
        activeView={activeView}
        tabs={tabs}
        canCloseTab={canCloseTab}
        selectWorkspace={selectWorkspace}
        selectOverview={selectOverview}
        openWorkspace={openWorkspace}
        navMode={navMode}
        navCollapsed={navCollapsed}
        onToggleNavCollapsed={onToggleNavCollapsed}
        onOpenSettings={() => setSettingsOpen(true)}
        accountActions={
          <RiskControlsButton collapsed={accountActionsCollapsed} />
        }
        activePanel={activePanel}
        nav={nav}
        {...shell}
      />
      <AtdSettings open={settingsOpen} onOpenChange={setSettingsOpen} />
    </SnapshotStreamProvider>
  );
};

export const Workspace = () => (
  <GroupRegistryProvider>
    <WorkspaceRepositoryProvider>
      <WorkspaceSelectionProvider>
        <WorkspaceConnected />
      </WorkspaceSelectionProvider>
    </WorkspaceRepositoryProvider>
  </GroupRegistryProvider>
);
